import React, { useEffect, useRef, useState } from 'react';
import { CalculatorIcon } from '@heroicons/react/24/outline';
import fuelIndexService, { type DriverFuelRow } from '../services/fuelIndex.service';
import { GWI_COMMISSION_PCT, formatMoney } from '../utils/gwiCommission';
import { DraggableModal } from './ui/DraggableModal';
import { Tooltip } from './ui/Tooltip';

const fuelFields = {
  Local: 'gulfLocal',
  OTR: 'gulfOtr',
  'SE OTR': 'southeastOtr',
} as const;
type FuelType = keyof typeof fuelFields;

const money = (cents: number) => `$${formatMoney(cents / 100)}`;

function CalculatorContent() {
  const [rate, setRate] = useState('');
  const [fuelType, setFuelType] = useState<FuelType>('Local');
  const [current, setCurrent] = useState<DriverFuelRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setCurrent(null);
    fuelIndexService.getDriverFuelRates(controller.signal).then((rows) => {
      if (controller.signal.aborted) return;
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      // Current Fuel Index contains historical readings. Use the newest dated
      // driver reading through today, never a future row or customer FSC.
      const latest = rows.reduce<DriverFuelRow | null>((selected, row) => {
        if (!row.beginDate || row.beginDate > today) return selected;
        return !selected?.beginDate || row.beginDate > selected.beginDate ? row : selected;
      }, null);
      setCurrent(latest);
      if (!latest) setError('No current driver fuel index is available.');
    }).catch(() => {
      if (!controller.signal.aborted) setError('Could not load the current driver fuel rates.');
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [attempt]);

  const rateCents = Math.round(Number(rate) * 100);
  const validRate = /^\d+(?:\.\d{0,2})?$/.test(rate.trim()) && Number.isSafeInteger(rateCents);
  const percent = current?.[fuelFields[fuelType]] ?? null;
  const validFuel = percent != null && Number.isFinite(percent) && percent >= 0;
  // Round each displayed line to cents, then sum those same cents for net pay.
  // Commission applies to the base rate; the driver keeps all of the FSC.
  const commission = validRate ? Math.round(rateCents * GWI_COMMISSION_PCT) : null;
  const fsc = validRate && validFuel && !loading ? Math.round(rateCents * percent / 100) : null;
  const net = fsc != null && commission != null ? rateCents + fsc - commission : null;
  const message = error ?? (!loading && current && !validFuel ? `No current FSC is available for ${fuelType}.` : null);
  const inputClass = 'h-9 w-full min-w-0 rounded border border-slate-300 bg-panel px-2 py-1.5 text-body dark:border-gray-600';

  return (
    <div className="space-y-4 text-sm text-body">
      <div className="grid grid-cols-[100px_minmax(0,1fr)] items-center gap-x-4 gap-y-3">
        <label htmlFor="driver-calculator-rate">Rate</label>
        <div className="relative">
          <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-2 flex items-center">$</span>
          <input
            id="driver-calculator-rate" autoFocus inputMode="decimal" placeholder="0.00"
            value={rate} onChange={(event) => setRate(event.target.value)}
            aria-invalid={rate !== '' && !validRate} className={`${inputClass} pl-6`}
          />
        </div>
        <label htmlFor="driver-calculator-fuel">Fuel Type</label>
        <select id="driver-calculator-fuel" value={fuelType} className={inputClass}
          onChange={(event) => setFuelType(event.target.value as FuelType)}>
          {Object.keys(fuelFields).map((type) => <option key={type}>{type}</option>)}
        </select>
      </div>
      {rate !== '' && !validRate && <p role="alert" className="text-red-600 dark:text-red-400">Enter a non-negative rate with up to two decimal places.</p>}
      {loading && <p role="status" className="text-muted">Loading current fuel index…</p>}
      {message && (
        <div role="alert" className="text-red-600 dark:text-red-400">
          {message}{' '}
          <button type="button" className="underline" onClick={() => setAttempt((value) => value + 1)}>Retry</button>
        </div>
      )}
      {current && <p className="text-xs text-muted">Current Fuel Index · Week of {current.weekOf}</p>}
      <dl className="grid grid-cols-[100px_1fr] gap-x-4 gap-y-3 border-t border-slate-200 pt-4 tabular-nums dark:border-gray-700" aria-live="polite">
        <dt>FSC</dt>
        <dd aria-label="FSC">{fsc == null ? '—' : `${money(fsc)} (${percent}%)`}</dd>
        <dt>Commission <span className="text-xs text-muted">(30%)</span></dt>
        <dd aria-label="Commission" className="text-red-600 dark:text-red-400">{commission == null ? '—' : `(${money(commission)})`}</dd>
        <dt className="font-semibold">Net Pay</dt>
        <dd aria-label="Net Pay" className="text-lg font-semibold">{net == null ? '—' : money(net)}</dd>
      </dl>
    </div>
  );
}

/** Header shortcut with a fresh, read-only scratch calculation on each open. */
export default function DriverRateCalculator({ className, iconClassName }: { className?: string; iconClassName?: string }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const close = () => { setOpen(false); buttonRef.current?.focus(); };
  return (
    <>
      <Tooltip content="Driver Rate Calculator" placement="bottom-end">
        <button ref={buttonRef} type="button" onClick={() => setOpen(true)} className={className}
          aria-label="Driver Rate Calculator" aria-haspopup="dialog" aria-expanded={open}>
          <CalculatorIcon className={iconClassName ?? 'h-5 w-5'} />
        </button>
      </Tooltip>
      {open && (
        <DraggableModal isOpen onClose={close} title="Driver Rate Calculator" width={380} className="max-w-[calc(100vw-16px)]">
          <CalculatorContent />
        </DraggableModal>
      )}
    </>
  );
}
